import { BookOpen, Brain, ChevronDown, ChevronUp, FolderInput, GraduationCap, MessageCircle, NotebookPen, Trash2, Users, Wrench, Zap } from 'lucide-react';
import type { JourneyItemTemplateFirestore, JourneyItemType } from '../services/journeyTypes';
import { journeyService } from '../services/journeyService';

interface DisplayJourneyItemProps {
    item: JourneyItemTemplateFirestore;
    chapterId: string;
    sectionId: string;
    onDeleted: () => void;
    onMove?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    onClick?: () => void;
}

const ITEM_TYPE_ICON: Record<JourneyItemType, React.ReactNode> = {
    'chat': <MessageCircle className="w-4 h-4" />,
    'check-in': <NotebookPen className="w-4 h-4" />,
    'story': <BookOpen className="w-4 h-4" />,
    'lesson': <GraduationCap className="w-4 h-4" />,
    'community-question': <Users className="w-4 h-4" />,
    'tool': <Wrench className="w-4 h-4" />,
};

const ITEM_TYPE_LABEL: Record<JourneyItemType, string> = {
    'chat': 'Chat',
    'check-in': 'Check-in',
    'story': 'Story',
    'lesson': 'Lesson',
    'community-question': 'Community Question',
    'tool': 'Tool',
};

const ITEM_TYPE_COLOR: Record<JourneyItemType, string> = {
    'chat': 'bg-blue-100 text-blue-800',
    'check-in': 'bg-green-100 text-green-800',
    'story': 'bg-purple-100 text-purple-800',
    'lesson': 'bg-indigo-100 text-indigo-800',
    'community-question': 'bg-pink-100 text-pink-800',
    'tool': 'bg-orange-100 text-orange-800',
};

export const DisplayJourneyItem = ({ item, chapterId, sectionId, onDeleted, onMove, onMoveUp, onMoveDown, canMoveUp = true, canMoveDown = true, onClick }: DisplayJourneyItemProps) => {
    const handleDelete = async () => {
        if (!item.id) return;
        const confirmed = window.confirm(
            `Delete item "${item.title || item.type}"? This cannot be undone.`,
        );
        if (!confirmed) return;
        await journeyService.deleteItem(chapterId, sectionId, item.id);
        onDeleted();
    };

    return (
        <div
            className="border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer"
            onClick={onClick}
        >
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                #{item.order}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ITEM_TYPE_COLOR[item.type]}`}>
                {ITEM_TYPE_ICON[item.type]}
                {ITEM_TYPE_LABEL[item.type]}
            </span>
            <div className="min-w-40 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                    {item.title || <span className="text-gray-400 italic">No title</span>}
                </p>
                {item.description && (
                    <p className="text-xs text-gray-500 truncate">{item.description}</p>
                )}
                {item.textTemplatingPrompt && (
                    <p className="text-xs text-indigo-500 truncate">{item.textTemplatingPrompt}</p>
                )}
                {item.id && (
                    <p className="text-xs font-mono text-gray-400 truncate">id: {item.id}</p>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                    <Brain className="w-3 h-3" />
                    {item.brainPoints}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Zap className="w-3 h-3" />
                    {item.energyCost}
                </span>
                {onMoveUp && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveUp();
                        }}
                        disabled={!canMoveUp}
                        className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title="Move up"
                    >
                        <ChevronUp className="w-4 h-4" />
                    </button>
                )}
                {onMoveDown && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveDown();
                        }}
                        disabled={!canMoveDown}
                        className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title="Move down"
                    >
                        <ChevronDown className="w-4 h-4" />
                    </button>
                )}
                {onMove && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMove();
                        }}
                        className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-500 hover:text-blue-600"
                        title="Move item to another section"
                    >
                        <FolderInput className="w-4 h-4" />
                    </button>
                )}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                    }}
                    className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-500 hover:text-red-600"
                    title="Delete item"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
